import { useEffect } from 'react'
import { Headphones } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCopilotStore } from '../../stores/copilotStore'
import { LiveTranscriptPanel } from './LiveTranscriptPanel'
import { RecordingPlayer } from '../call-detail/RecordingPlayer'
import { useCallTranscript } from '../../api/telemetry'

interface Props {
  callSid: string
  // Persisted call_events from the detail API. When omitted or empty, the
  // store is assumed already-populated from the live SSE session (the inline
  // post-hangup case) and we render without re-hydrating — re-hydration would
  // reset the slice and wipe the live data.
  events?: Array<{ event_type: string; timestamp?: number; payload?: Record<string, unknown> }>
}

export function CopilotSessionView({ callSid, events }: Props) {
  const { t } = useTranslation('calls')
  const hydrate = useCopilotStore((s) => s.hydrate)
  // recording_url arrives via the copilot.recording_available event (hydrated
  // from `events` on the detail page, or live on the inline post-hangup view).
  const liveRecordingUrl = useCopilotStore((s) => s.byCallSid[callSid]?.recordingUrl)

  // The authoritative post-call transcript is Deepgram's recording-grounded
  // transcription, persisted to calls.transcript_messages by the insights
  // service. The live copilot.transcript.* events do NOT carry it — and the
  // PSTN-bridge flow never emits them at all — so the live panel alone shows
  // "Waiting for audio…" forever. Prefer the persisted transcript; fall back to
  // the live store for the inline post-hangup view (before extraction runs).
  const { data: persisted } = useCallTranscript(callSid)
  const persistedTranscript = persisted?.transcript ?? []
  const recordingUrl = liveRecordingUrl || persisted?.recording_url

  useEffect(() => {
    if (!events || events.length === 0) return
    hydrate(callSid, events)
  }, [callSid, events, hydrate])

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
        <Headphones className="h-5 w-5" />
        {t('detail.copilot.sessionTitle')}
      </h2>
      {recordingUrl && <RecordingPlayer callSid={callSid} />}
      <div className="min-h-[400px] lg:h-[calc(100vh-12rem)]">
        {persistedTranscript.length > 0 ? (
          <section className="flex h-full flex-col rounded-lg border border-border bg-surface-card">
            <header className="border-b border-border px-4 py-2">
              <h3 className="text-sm font-semibold text-text-primary">
                {t('detail.copilot.transcriptTitle')}
              </h3>
            </header>
            <ul className="flex-1 space-y-2 overflow-y-auto px-4 py-3 text-sm">
              {persistedTranscript.map((entry, i) => {
                // call.transcript.user = prospect; everything else = rep. The
                // recording-grounded transcript may label every turn the same
                // when the source is a mono conference mix; the dual-channel
                // bridge recording is what separates the two speakers.
                const isProspect = entry.event_type.includes('user')
                return (
                  <li
                    key={i}
                    className={
                      isProspect
                        ? 'rounded bg-surface-elevated px-3 py-2'
                        : 'rounded bg-primary/5 px-3 py-2'
                    }
                  >
                    <span
                      className={
                        isProspect
                          ? 'mr-2 text-xs font-semibold uppercase text-text-secondary'
                          : 'mr-2 text-xs font-semibold uppercase text-primary'
                      }
                    >
                      {isProspect ? 'prospect' : 'rep'}
                    </span>
                    <span className="text-text-primary">{entry.text}</span>
                  </li>
                )
              })}
            </ul>
          </section>
        ) : (
          <LiveTranscriptPanel callSid={callSid} readOnly />
        )}
      </div>
    </section>
  )
}
